package com.siriusxm.pia.views.unifiedaggregator.management

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.service.registry.Contributor
import com.siriusxm.service.registry.Team
import com.siriusxm.service.registry.TeamStatus
import com.siriusxm.unifiedcontent.console.ucp.UcpTeamClient
import com.siriusxm.unifiedcontent.console.ucp.UcpTeamResult
import unifiedcontent.aggregator.ConsumerDetails
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.ATarget
import org.jetbrains.compose.web.attributes.target
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Text

/**
 * The Team box on a producer or consumer detail page (UCA-1212).
 *
 * Shows the Unified Content Permissions (UCP) team the record is linked to by [teamId], with its
 * members and their roles, read-only, with team admins marked. The team description is the box
 * header's instruction line. Each member's name links to their page in the permissions app.
 * Team membership is administered entirely in UCP, so the only way to change it from here is the
 * box menu's "Open in Permissions" action. A roster says who is on the team, not what each member
 * may do; the aggregator still decides every operation on its own.
 *
 * Nothing is rendered when the record is not linked to a team. Otherwise each state is shown
 * distinctly: loading, team not found, UCP unavailable, inactive team, and a team with no members.
 *
 * On a consumer ([consumer] set), a viewer who may manage the aggregator's policies in UCP also gets
 * "Sync Permissions", which grants the team target administration, backfill and replay for this
 * consumer's accounts (UCA-1240).
 *
 * @param aggregator the aggregator service, for the application context
 * @param teamId the stored UCP team id on the producer or consumer, or null when not linked
 * @param consumer the consumer the box is shown on, or null on a producer
 */
@Composable
fun TeamBox(aggregator: AggregatorService, teamId: String?, consumer: ConsumerDetails? = null) {
    val client = remember(aggregator) {
        UcpTeamClient(aggregator.context, aggregator.context.configuration.ucpApiUrl)
    }
    var result by remember { mutableStateOf<UcpTeamResult?>(null) }

    LaunchedEffect(teamId) {
        result = null
        if (!teamId.isNullOrBlank()) {
            result = client.team(teamId)
        }
    }

    val canSync = consumer != null && rememberCanSyncConsumerPermissions(aggregator, teamId)
    var syncing by remember { mutableStateOf(false) }

    if (teamId.isNullOrBlank()) return

    val loaded = (result as? UcpTeamResult.Found)?.team
    val permissionsUrl = aggregator.context.configuration.permissionsBaseUrl.trimEnd('/')

    // "Team: <name>" once the team has loaded; plain "Team" while loading or when it can't be shown.
    box(loaded?.let { "Team: ${it.name}" } ?: "Team", {
        // The member table runs edge to edge.
        paddedContent = false
        // The description and the menu need the loaded team, so they appear once it has loaded.
        if (loaded != null) {
            header({ instruction = loaded.description?.takeIf { it.isNotBlank() } })
            menu {
                subAction {
                    title = "Open in Permissions"
                    action {
                        window.open("$permissionsUrl/teams/${encodeURIComponent(loaded.id)}", "_blank", "noopener")
                    }
                }
                if (canSync) {
                    subAction {
                        title = "Sync Permissions"
                        action { syncing = true }
                    }
                }
            }
        }
    }) {
        when (val current = result) {
            null -> boxSpinner()
            is UcpTeamResult.NotFound ->
                boxMessage("UCP has no team with the id \"$teamId\". Check the team id on this service.")
            is UcpTeamResult.Unavailable ->
                boxMessage("Team details are unavailable right now: ${current.reason}")
            is UcpTeamResult.Found -> TeamDetails(current.team, permissionsUrl)
        }
    }

    if (syncing && consumer != null && loaded != null) {
        SyncConsumerPermissionsDialog(aggregator, consumer, loaded.id) { syncing = false }
    }
}

/**
 * The read-only member list, preceded by a notice when the team is inactive in UCP. The box is already
 * titled "Team: <name>", so the name is not repeated; the menu opens the team in the permissions app.
 * Dev and test do not mount that app, so its links always open production, in a separate tab.
 */
@Composable
private fun TeamDetails(team: Team, permissionsUrl: String) {
    if (team.status != TeamStatus.ACTIVE) {
        boxMessage("This team is inactive in UCP.")
    }

    val members = team.members.orEmpty()
    if (members.isEmpty()) {
        boxMessage("This team has no members.")
    } else {
        table<Contributor> {
            // Admins first, then by name, so the people who can change membership in UCP stand out.
            items(members.sortedWith(compareByDescending<Contributor> { it.isAdminOn(team) }.thenBy { it.name ?: it.id }))
            column("Name") {
                content {
                    A(href = "$permissionsUrl/contributors/${encodeURIComponent(it.id)}", {
                        target(ATarget.Blank)
                        attr("rel", "noopener")
                    }) { Text(it.name ?: it.id) }
                }
            }
            column("Title") { content { Text(it.title.orEmpty()) } }
            column("Role") {
                content {
                    Text(it.roleOn(team))
                    if (it.isAdminOn(team)) {
                        Text(" ")
                        Badge("Admin", BadgeColor.BLUE)
                    }
                }
            }
        }
    }
}

/** Whether the member is an admin of [team] in UCP (`isTeamAdmin`, separate from the free-text role). */
private fun Contributor.isAdminOn(team: Team): Boolean =
    roles?.any { it.teamId == team.id && it.isTeamAdmin == true } == true

/** The member's free-text role on [team]; the roles list on a team's members only holds that team. */
private fun Contributor.roleOn(team: Team): String =
    roles?.firstOrNull { it.teamId == team.id }?.role.orEmpty()
